// Milestones hook — feeds the roadmap panel, the milestone PICKERS (Focus's
// widget, the chat readiness card) and the "make this a milestone?" suggestion
// banner. One read endpoint (`GET /milestones`), and the UI never fans out.
//
// Two shapes come back from it. The Roadmap asks for the per-milestone issue
// counts; the pickers pass `{ counts: false }` and get rows WITHOUT them,
// because computing them costs the server a full enumeration of every issue in
// the project (see `UseMilestonesOptions.counts`). Reads of `issueCount` /
// `closedCount` / `weight` must therefore handle the key being absent.
//
// Requests are coalesced module-level, not per mount — see `flights`.

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
  DashboardMilestoneSchema,
  MilestoneRoadmapResponseSchema,
  type CreateMilestoneRequest,
  type DashboardIssue,
  type DashboardMilestone,
  type MilestoneRoadmapResponse,
} from "@loom/shared/dashboard";

import { httpGet, httpPost, httpPut, UnauthorizedError } from "../lib/http";

const CreateMilestoneResponseSchema = z.object({
  milestone: DashboardMilestoneSchema,
});

const AssignIssueMilestoneResponseSchema = z.object({
  issue: z.unknown(),
});

export interface MilestonesState {
  readonly data: MilestoneRoadmapResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
  readonly create: (
    request: CreateMilestoneRequest,
  ) => Promise<DashboardMilestone>;
  readonly assignToIssue: (
    issueIid: number,
    milestoneId: number | null,
  ) => Promise<void>;
  readonly creating: boolean;
  readonly assigning: boolean;
}

export interface UseMilestonesOptions {
  /**
   * Fetch the per-milestone issue counts. Default true.
   *
   * The PICKERS pass false: they render the title plus a "closed" sub-label and
   * read no count, while computing the counts costs the server a full
   * offset-paginated enumeration of every issue in the project — 8–21s on a
   * 978-issue project, on every request. Rows then arrive with `issueCount` /
   * `closedCount` / `weight` ABSENT.
   */
  readonly counts?: boolean;
}

/**
 * Short-lived request coalescer, module-level so it outlives a remount.
 *
 * Remounting is routine here, not exceptional: FocusFrame rebuilds the widget
 * subtree on every step-key change and AgentAskPin keys on `askId`, so
 * production shows clusters of 2–3 identical requests inside three minutes,
 * each paying the full enumeration. Entries share the in-flight PROMISE rather
 * than only the settled value, which is what serves the remount from the
 * request already in the air.
 *
 * Deliberately not an AbortController: aborting would cancel a fetch the very
 * next mount immediately re-issues, doubling server work in exactly the
 * clustered case. A REJECTED flight is evicted immediately — a cached failure
 * handed to every mount inside the TTL would turn a transient 500 into a
 * minute-long dead picker.
 *
 * Not keyed by user, unlike a server-side cache, and that is safe for the
 * reason a server-side one would not be: this map lives in ONE tab's module
 * scope, holding only what that tab's own signed-in user just fetched on their
 * own token. A user change means an OAuth navigation, which reloads the
 * document and takes the map with it.
 */
const FLIGHT_TTL_MS = 30_000;
const flights = new Map<
  string,
  { at: number; promise: Promise<MilestoneRoadmapResponse> }
>();

const flightKey = (projectId: number, counts: boolean): string =>
  `${projectId}:${counts ? "counts" : "none"}`;

const fetchMilestones = (
  projectId: number,
  counts: boolean,
): Promise<MilestoneRoadmapResponse> => {
  const key = flightKey(projectId, counts);
  const hit = flights.get(key);
  if (hit !== undefined && Date.now() - hit.at < FLIGHT_TTL_MS) return hit.promise;
  const promise = httpGet(
    `/api/projects/${projectId}/milestones${counts ? "" : "?counts=none"}`,
    MilestoneRoadmapResponseSchema,
  );
  flights.set(key, { at: Date.now(), promise });
  promise.catch(() => {
    if (flights.get(key)?.promise === promise) flights.delete(key);
  });
  return promise;
};

/** Drop both variants for a project — a write just invalidated them. */
const evictMilestones = (projectId: number): void => {
  flights.delete(flightKey(projectId, true));
  flights.delete(flightKey(projectId, false));
};

export const useMilestones = (
  projectId: number,
  options?: UseMilestonesOptions,
): MilestonesState => {
  const [data, setData] = useState<MilestoneRoadmapResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [assigning, setAssigning] = useState<boolean>(false);
  const [tick, setTick] = useState<number>(0);
  const aborted = useRef(false);
  const wantCounts = options?.counts !== false;

  useEffect(() => {
    aborted.current = false;
    setLoading(true);
    setError(null);
    fetchMilestones(projectId, wantCounts)
      .then((value) => {
        if (aborted.current) return;
        setData(value);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        // `loading` is cleared FIRST and unconditionally. It used to be left
        // true on the 401 leg, so an expired session pinned the picker on
        // "Loading milestones…" forever with no control on screen — the
        // redirect the UnauthorizedError triggers elsewhere is not something
        // this widget may assume happened.
        setLoading(false);
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      aborted.current = true;
    };
  }, [projectId, tick, wantCounts]);

  const refetch = useCallback((): void => {
    evictMilestones(projectId);
    setTick((n) => n + 1);
  }, [projectId]);

  const create = useCallback(
    async (request: CreateMilestoneRequest): Promise<DashboardMilestone> => {
      setCreating(true);
      try {
        const out = await httpPost(
          `/api/projects/${projectId}/milestones`,
          request,
          CreateMilestoneResponseSchema,
        );
        evictMilestones(projectId);
        setTick((n) => n + 1);
        return out.milestone;
      } finally {
        setCreating(false);
      }
    },
    [projectId],
  );

  const assignToIssue = useCallback(
    async (issueIid: number, milestoneId: number | null): Promise<void> => {
      setAssigning(true);
      try {
        await httpPut(
          `/api/projects/${projectId}/issues/${issueIid}/milestone`,
          { milestoneId },
          AssignIssueMilestoneResponseSchema,
        );
        evictMilestones(projectId);
        setTick((n) => n + 1);
      } finally {
        setAssigning(false);
      }
    },
    [projectId],
  );

  return {
    data,
    loading,
    error,
    refetch,
    create,
    assignToIssue,
    creating,
    assigning,
  };
};

/**
 * Convenience helper: derive a "make this a milestone from issue #N" default
 * title + date window. The roadmap panel offers a one-click affordance using
 * this when the complexity suggestion fires.
 */
export const milestoneFromIssue = (
  issue: DashboardIssue,
): CreateMilestoneRequest => {
  const now = new Date();
  const due = new Date(now);
  due.setDate(due.getDate() + 28);
  const fmt = (d: Date): string => d.toISOString().slice(0, 10);
  return {
    title: issue.title.slice(0, 240),
    description: `Auto-suggested from issue ${issue.key}: ${issue.title}\n\nUse this milestone to decompose the original story into smaller, deliverable pieces.`,
    startDate: fmt(now),
    dueDate: fmt(due),
  };
};
